/**
 * Neutral, reusable selection logic for the shared decline-reason model.
 * Pure module (no React, no i18next runtime) — Vitest-friendly. Shared by the post-login feedback
 * dialog, the manual quote-decline dialog, and the read-only reason display cards.
 */

import {DeclineReason} from "@/api/data-contracts";
import {DeclineReasonI18N} from "@/translations";

/** Maps each {@link DeclineReason} enum value to its neutral i18n label key. */
export const DECLINE_REASON_LABEL_KEYS: Record<DeclineReason, string> = {
    [DeclineReason.EXTERNAL_TENDER_NOT_AWARDED]: DeclineReasonI18N.EXTERNAL_TENDER_NOT_AWARDED,
    [DeclineReason.END_CUSTOMER_REQUIREMENTS_CHANGED]: DeclineReasonI18N.END_CUSTOMER_REQUIREMENTS_CHANGED,
    [DeclineReason.AWARDED_OFF_PLATFORM]: DeclineReasonI18N.AWARDED_OFF_PLATFORM,
    [DeclineReason.IN_HOUSE_PRODUCTION]: DeclineReasonI18N.IN_HOUSE_PRODUCTION,
    [DeclineReason.PRICE_NOT_COMPETITIVE]: DeclineReasonI18N.PRICE_NOT_COMPETITIVE,
    [DeclineReason.NO_REASON_GIVEN]: DeclineReasonI18N.NO_REASON_GIVEN,
    [DeclineReason.OTHER]: DeclineReasonI18N.OTHER,
    [DeclineReason.AWARDED_TO_OTHER_SUPPLIER]: DeclineReasonI18N.AWARDED_TO_OTHER_SUPPLIER
};

/**
 * The subset of {@link DeclineReason} values a user may pick manually (in the manual quote-decline
 * dialog or the post-login feedback dialog). Excludes {@link DeclineReason.AWARDED_TO_OTHER_SUPPLIER},
 * which the backend sets exclusively when a competing quote wins the award — selecting it manually is
 * rejected server-side, so it must not appear as a selectable radio option.
 */
export const SELECTABLE_DECLINE_REASONS: DeclineReason[] = Object.values(DeclineReason).filter(
    (reason) => reason !== DeclineReason.AWARDED_TO_OTHER_SUPPLIER
);

/**
 * Returns true when a non-blank free text is mandatory for the given reason — i.e. only for
 * {@link DeclineReason.OTHER}. Drives the dynamic "(Pflichtfeld)" vs "(optional)" free-text label;
 * {@code undefined} (no reason chosen yet) is treated as not mandatory.
 *
 * @param reason - the currently selected reason, or undefined when none is selected yet
 */
export function isFreeTextMandatory(reason: DeclineReason | undefined): boolean {
    return reason === DeclineReason.OTHER;
}
